// Application Components
import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { object, string } from 'yup'
import {
    translateField,
    phoneNumberEvaluationField,
} from '../../functions/field-functions'
import { extractSubmissionForm } from '../../functions/form-functions'

//// Application Slices
import { selectSettings } from '../../reducers/settingSlice'
import {
    updateFormValidity,
    updateFormSystem,
    selectForms,
} from '../../reducers/formSlice'

//// Application Components
import AddContactFormComponent from '../../components/navigation-components/add-contact-form-component'
import ButtonComponent from '../../components/conditional-components/button-component'
import LoadingComponent from '../../components/simple-components/loading-component'
import NotificationComponent from '../../components/conditional-components/notification-component'
import AdditionalContactInformationForm from '../../components/generic-components/additional-contact-information-component'

//// Carbon Components
import { Form } from '@carbon/react'

//// Carbon Icons

//// Static Constants

//////// Component
const ContactInformationForm = ({ instance }) => {
    //////// Hooks
    const dispatch = useDispatch()
    const navigate = useNavigate()

    //////// State
    const [loading, setLoading] = useState(false)
    const [notification, setNotification] = useState({})

    //////// Slices
    const settingsData = useSelector(selectSettings)
    const formsData = useSelector(selectForms)

    //// Slice Data
    const digital_name = settingsData?.['form']?.['digital_name']
    const submit_route = settingsData?.['form']?.['submit_route']
    const fields = settingsData?.['form']?.['fields']

    //////// Search Params

    //////// Methods

    // Validate Data Submission
    const validation = async (submit) => {
        // Validate Form Submission Data Against Schema
        try {
            // Schema Object
            const schema = {}
            if (
                formsData?.[digital_name]?.auxiliary?.practice_location ===
                    false &&
                formsData?.[digital_name]?.auxiliary?.satellite_location ===
                    false
            ) {
                schema.yup = object({
                    addr_line1MA: string()
                        .min(0)
                        .max(30)
                        .required(
                            'This field is required to be entered or selected.'
                        ),
                    addr_line2MA: string().min(0).max(30),
                    addr_line3MA: string().min(0).max(40),
                    addr_zipMA: string()
                        .min(0)
                        .max(10)
                        .required(
                            'This field is required to be entered or selected.'
                        ),
                    addr_ctyMA: string()
                        .min(0)
                        .max(20)
                        .required(
                            'This field is required to be entered or selected.'
                        ),
                    st_cdeMA: string().required(
                        'This field is required to be entered or selected.'
                    ),
                    cntyMA: string().required(
                        'This field is required to be entered or selected.'
                    ),
                    cntry_cdeMA: string().required(
                        'This field is required to be entered or selected.'
                    ),
                    phne_nbrMA: string()
                        .min(0)
                        .max(16)
                        .test('phn', 'Must be a valid phone number', () =>
                            phoneNumberEvaluationField(
                                formsData,
                                digital_name,
                                'phne_nbrMA'
                            )
                        ),
                    e_mail_addrMA: string().email().min(0).max(100),
                    phne_extnMA: string().min(0).max(6),
                })
                schema.valid = {
                    addr_line1MA: false,
                    addr_line1MAErrMsg: '',
                    addr_line2MA: false,
                    addr_line2MAErrMsg: '',
                    addr_line3MA: false,
                    addr_line3MAErrMsg: '',
                    addr_zipMA: false,
                    addr_zipMAErrMsg: '',
                    addr_ctyMA: false,
                    addr_ctyMAErrMsg: '',
                    st_cdeMA: false,
                    st_cdeMAErrMsg: '',
                    cntyMA: false,
                    cntyMAErrMsg: '',
                    cntry_cdeMA: false,
                    cntry_cdeMAErrMsg: '',
                    phne_nbrMA: false,
                    phne_nbrMAErrMsg: '',
                    e_mail_addrMA: false,
                    e_mail_addrMAErrMsg: '',
                    phne_extnMA: false,
                    phne_extnMAErrMsg: '',
                }
            } else if (
                formsData?.[digital_name]?.auxiliary?.practice_location ===
                    true &&
                formsData?.[digital_name]?.auxiliary?.satellite_location ===
                    false
            ) {
                schema.yup = object({
                    addr_line1MA: string()
                        .min(0)
                        .max(30)
                        .required(
                            'This field is required to be entered or selected.'
                        ),
                    addr_line2MA: string().min(0).max(30),
                    addr_line3MA: string().min(0).max(40),
                    addr_zipMA: string()
                        .min(0)
                        .max(10)
                        .required(
                            'This field is required to be entered or selected.'
                        ),
                    addr_ctyMA: string()
                        .min(0)
                        .max(20)
                        .required(
                            'This field is required to be entered or selected.'
                        ),
                    st_cdeMA: string().required(
                        'This field is required to be entered or selected.'
                    ),
                    cntyMA: string().required(
                        'This field is required to be entered or selected.'
                    ),
                    cntry_cdeMA: string().required(
                        'This field is required to be entered or selected.'
                    ),
                    phne_nbrMA: string()
                        .min(0)
                        .max(16)
                        .test('phn', 'Must be a valid phone number', () =>
                            phoneNumberEvaluationField(
                                formsData,
                                digital_name,
                                'phne_nbrMA'
                            )
                        ),
                    e_mail_addrMA: string().email().min(0).max(100),
                    phne_extnMA: string().min(0).max(6),
                    addr_line1PL: string()
                        .min(0)
                        .max(30)
                        .required(
                            'This field is required to be entered or selected.'
                        ),
                    addr_line2PL: string().min(0).max(30),
                    addr_line3PL: string().min(0).max(40),
                    addr_zipPL: string()
                        .min(0)
                        .max(10)
                        .required(
                            'This field is required to be entered or selected.'
                        ),
                    addr_ctyPL: string()
                        .min(0)
                        .max(20)
                        .required(
                            'This field is required to be entered or selected.'
                        ),
                    st_cdePL: string().required(
                        'This field is required to be entered or selected.'
                    ),
                    cntyPL: string().required(
                        'This field is required to be entered or selected.'
                    ),
                    cntry_cdePL: string().required(
                        'This field is required to be entered or selected.'
                    ),
                    phne_nbrPL: string()
                        .min(0)
                        .max(16)
                        .test('phn', 'Must be a valid phone number', () =>
                            phoneNumberEvaluationField(
                                formsData,
                                digital_name,
                                'phne_nbrPL'
                            )
                        ),
                    e_mail_addrPL: string().email().min(0).max(100),
                    phne_extnPL: string().min(0).max(6),
                })
                schema.valid = {
                    addr_line1MA: false,
                    addr_line1MAErrMsg: '',
                    addr_line2MA: false,
                    addr_line2MAErrMsg: '',
                    addr_line3MA: false,
                    addr_line3MAErrMsg: '',
                    addr_zipMA: false,
                    addr_zipMAErrMsg: '',
                    addr_ctyMA: false,
                    addr_ctyMAErrMsg: '',
                    st_cdeMA: false,
                    st_cdeMAErrMsg: '',
                    cntyMA: false,
                    cntyMAErrMsg: '',
                    cntry_cdeMA: false,
                    cntry_cdeMAErrMsg: '',
                    phne_nbrMA: false,
                    phne_nbrMAErrMsg: '',
                    e_mail_addrMA: false,
                    e_mail_addrMAErrMsg: '',
                    phne_extnMA: false,
                    phne_extnMAErrMsg: '',
                    addr_line1PL: false,
                    addr_line1PLErrMsg: '',
                    addr_line2PL: false,
                    addr_line2PLErrMsg: '',
                    addr_line3PL: false,
                    addr_line3PLErrMsg: '',
                    addr_zipPL: false,
                    addr_zipPLErrMsg: '',
                    addr_ctyPL: false,
                    addr_ctyPLErrMsg: '',
                    st_cdePL: false,
                    st_cdePLErrMsg: '',
                    cntyPL: false,
                    cntyPLErrMsg: '',
                    cntry_cdePL: false,
                    cntry_cdePLErrMsg: '',
                    phne_nbrPL: false,
                    phne_nbrPLErrMsg: '',
                    e_mail_addrPL: false,
                    e_mail_addrPLErrMsg: '',
                    phne_extnPL: false,
                    phne_extnPLErrMsg: '',
                }
            } else if (
                formsData?.[digital_name]?.auxiliary?.practice_location ===
                    false &&
                formsData?.[digital_name]?.auxiliary?.satellite_location ===
                    true
            ) {
                schema.yup = object({
                    addr_line1MA: string()
                        .min(0)
                        .max(30)
                        .required(
                            'This field is required to be entered or selected.'
                        ),
                    addr_line2MA: string().min(0).max(30),
                    addr_line3MA: string().min(0).max(40),
                    addr_zipMA: string()
                        .min(0)
                        .max(10)
                        .required(
                            'This field is required to be entered or selected.'
                        ),
                    addr_ctyMA: string()
                        .min(0)
                        .max(20)
                        .required(
                            'This field is required to be entered or selected.'
                        ),
                    st_cdeMA: string().required(
                        'This field is required to be entered or selected.'
                    ),
                    cntyMA: string().required(
                        'This field is required to be entered or selected.'
                    ),
                    cntry_cdeMA: string().required(
                        'This field is required to be entered or selected.'
                    ),
                    phne_nbrMA: string()
                        .min(0)
                        .max(16)
                        .test('phn', 'Must be a valid phone number', () =>
                            phoneNumberEvaluationField(
                                formsData,
                                digital_name,
                                'phne_nbrMA'
                            )
                        ),
                    e_mail_addrMA: string().email().min(0).max(100),
                    phne_extnMA: string().min(0).max(6),
                    addr_line1SL: string()
                        .min(0)
                        .max(30)
                        .required(
                            'This field is required to be entered or selected.'
                        ),
                    addr_line2SL: string().min(0).max(30),
                    addr_line3SL: string().min(0).max(40),
                    addr_zipSL: string()
                        .min(0)
                        .max(10)
                        .required(
                            'This field is required to be entered or selected.'
                        ),
                    addr_ctySL: string()
                        .min(0)
                        .max(20)
                        .required(
                            'This field is required to be entered or selected.'
                        ),
                    st_cdeSL: string().required(
                        'This field is required to be entered or selected.'
                    ),
                    cntySL: string().required(
                        'This field is required to be entered or selected.'
                    ),
                    cntry_cdeSL: string().required(
                        'This field is required to be entered or selected.'
                    ),
                    phne_nbrSL: string()
                        .min(0)
                        .max(16)
                        .test('phn', 'Must be a valid phone number', () =>
                            phoneNumberEvaluationField(
                                formsData,
                                digital_name,
                                'phne_nbrSL'
                            )
                        ),
                    e_mail_addrSL: string().email().min(0).max(100),
                    phne_extnSL: string().min(0).max(6),
                })
                schema.valid = {
                    addr_line1MA: false,
                    addr_line1MAErrMsg: '',
                    addr_line2MA: false,
                    addr_line2MAErrMsg: '',
                    addr_line3MA: false,
                    addr_line3MAErrMsg: '',
                    addr_zipMA: false,
                    addr_zipMAErrMsg: '',
                    addr_ctyMA: false,
                    addr_ctyMAErrMsg: '',
                    st_cdeMA: false,
                    st_cdeMAErrMsg: '',
                    cntyMA: false,
                    cntyMAErrMsg: '',
                    cntry_cdeMA: false,
                    cntry_cdeMAErrMsg: '',
                    phne_nbrMA: false,
                    phne_nbrMAErrMsg: '',
                    e_mail_addrMA: false,
                    e_mail_addrMAErrMsg: '',
                    phne_extnMA: false,
                    phne_extnMAErrMsg: '',
                    addr_line1SL: false,
                    addr_line1SLErrMsg: '',
                    addr_line2SL: false,
                    addr_line2SLErrMsg: '',
                    addr_line3SL: false,
                    addr_line3SLErrMsg: '',
                    addr_zipSL: false,
                    addr_zipSLErrMsg: '',
                    addr_ctySL: false,
                    addr_ctySLErrMsg: '',
                    st_cdeSL: false,
                    st_cdeSLErrMsg: '',
                    cntySL: false,
                    cntySLErrMsg: '',
                    cntry_cdeSL: false,
                    cntry_cdeSLErrMsg: '',
                    phne_nbrSL: false,
                    phne_nbrSLErrMsg: '',
                    e_mail_addrSL: false,
                    e_mail_addrSLErrMsg: '',
                    phne_extnSL: false,
                    phne_extnSLErrMsg: '',
                }
            } else if (
                formsData?.[digital_name]?.auxiliary?.practice_location ===
                    true &&
                formsData?.[digital_name]?.auxiliary?.satellite_location ===
                    true
            ) {
                schema.yup = object({
                    addr_line1MA: string()
                        .min(0)
                        .max(30)
                        .required(
                            'This field is required to be entered or selected.'
                        ),
                    addr_line2MA: string().min(0).max(30),
                    addr_line3MA: string().min(0).max(40),
                    addr_zipMA: string()
                        .min(0)
                        .max(10)
                        .required(
                            'This field is required to be entered or selected.'
                        ),
                    addr_ctyMA: string()
                        .min(0)
                        .max(20)
                        .required(
                            'This field is required to be entered or selected.'
                        ),
                    st_cdeMA: string().required(
                        'This field is required to be entered or selected.'
                    ),
                    cntyMA: string().required(
                        'This field is required to be entered or selected.'
                    ),
                    cntry_cdeMA: string().required(
                        'This field is required to be entered or selected.'
                    ),
                    phne_nbrMA: string()
                        .min(0)
                        .max(16)
                        .test('phn', 'Must be a valid phone number', () =>
                            phoneNumberEvaluationField(
                                formsData,
                                digital_name,
                                'phne_nbrMA'
                            )
                        ),
                    e_mail_addrMA: string().email().min(0).max(100),
                    phne_extnMA: string().min(0).max(6),
                    addr_line1PL: string()
                        .min(0)
                        .max(30)
                        .required(
                            'This field is required to be entered or selected.'
                        ),
                    addr_line2PL: string().min(0).max(30),
                    addr_line3PL: string().min(0).max(40),
                    addr_zipPL: string().min(0).max(10).required(10),
                    addr_ctyPL: string()
                        .min(0)
                        .max(20)
                        .required(
                            'This field is required to be entered or selected.'
                        ),
                    st_cdePL: string().required(
                        'This field is required to be entered or selected.'
                    ),
                    cntyPL: string().required(
                        'This field is required to be entered or selected.'
                    ),
                    cntry_cdePL: string().required(
                        'This field is required to be entered or selected.'
                    ),
                    phne_nbrPL: string()
                        .min(0)
                        .max(16)
                        .test('phn', 'Must be a valid phone number', () =>
                            phoneNumberEvaluationField(
                                formsData,
                                digital_name,
                                'phne_nbrPL'
                            )
                        ),
                    e_mail_addrPL: string().email().min(0).max(100),
                    phne_extnPL: string().min(0).max(6),
                    addr_line1SL: string()
                        .min(0)
                        .max(30)
                        .required(
                            'This field is required to be entered or selected.'
                        ),
                    addr_line2SL: string().min(0).max(30),
                    addr_line3SL: string().min(0).max(40),
                    addr_zipSL: string().min(0).max(10).required(10),
                    addr_ctySL: string()
                        .min(0)
                        .max(20)
                        .required(
                            'This field is required to be entered or selected.'
                        ),
                    st_cdeSL: string().required(
                        'This field is required to be entered or selected.'
                    ),
                    cntySL: string().required(
                        'This field is required to be entered or selected.'
                    ),
                    cntry_cdeSL: string().required(
                        'This field is required to be entered or selected.'
                    ),
                    phne_nbrSL: string()
                        .min(0)
                        .max(16)
                        .test('phn', 'Must be a valid phone number', () =>
                            phoneNumberEvaluationField(
                                formsData,
                                digital_name,
                                'phne_nbrSL'
                            )
                        ),
                    e_mail_addrSL: string().email().min(0).max(100),
                    phne_extnSL: string().min(0).max(6),
                })
                schema.valid = {
                    addr_line1MA: false,
                    addr_line1MAErrMsg: '',
                    addr_line2MA: false,
                    addr_line2MAErrMsg: '',
                    addr_line3MA: false,
                    addr_line3MAErrMsg: '',
                    addr_zipMA: false,
                    addr_zipMAErrMsg: '',
                    addr_ctyMA: false,
                    addr_ctyMAErrMsg: '',
                    st_cdeMA: false,
                    st_cdeMAErrMsg: '',
                    cntyMA: false,
                    cntyMAErrMsg: '',
                    cntry_cdeMA: false,
                    cntry_cdeMAErrMsg: '',
                    phne_nbrMA: false,
                    phne_nbrMAErrMsg: '',
                    e_mail_addrMA: false,
                    e_mail_addrMAErrMsg: '',
                    phne_extnMA: false,
                    phne_extnMAErrMsg: '',
                    addr_line1PL: false,
                    addr_line1PLErrMsg: '',
                    addr_line2PL: false,
                    addr_line2PLErrMsg: '',
                    addr_line3PL: false,
                    addr_line3PLErrMsg: '',
                    addr_zipPL: false,
                    addr_zipPLErrMsg: '',
                    addr_ctyPL: false,
                    addr_ctyPLErrMsg: '',
                    st_cdePL: false,
                    st_cdePLErrMsg: '',
                    cntyPL: false,
                    cntyPLErrMsg: '',
                    cntry_cdePL: false,
                    cntry_cdePLErrMsg: '',
                    phne_nbrPL: false,
                    phne_nbrPLErrMsg: '',
                    e_mail_addrPL: false,
                    e_mail_addrPLErrMsg: '',
                    phne_extnPL: false,
                    phne_extnPLErrMsg: '',
                    addr_line1SL: false,
                    addr_line1SLErrMsg: '',
                    addr_line2SL: false,
                    addr_line2SLErrMsg: '',
                    addr_line3SL: false,
                    addr_line3SLErrMsg: '',
                    addr_zipSL: false,
                    addr_zipSLErrMsg: '',
                    addr_ctySL: false,
                    addr_ctySLErrMsg: '',
                    st_cdeSL: false,
                    st_cdeSLErrMsg: '',
                    cntySL: false,
                    cntySLErrMsg: '',
                    cntry_cdeSL: false,
                    cntry_cdeSLErrMsg: '',
                    phne_nbrSL: false,
                    phne_nbrSLErrMsg: '',
                    e_mail_addrSL: false,
                    e_mail_addrSLErrMsg: '',
                    phne_extnSL: false,
                    phne_extnSLErrMsg: '',
                }
            }
            await schema.yup.validate(
                { ...submit },
                { strict: true, abortEarly: false }
            )
            dispatch(
                updateFormValidity({
                    digital_name: digital_name,
                    errors: {},
                })
            )
            return true
        } catch (err) {
            const errors = {}
            if (err?.inner?.length > 0) {
                await err.inner.forEach(async (el) => {
                    if (el?.path) {
                        errors[el?.path] = true
                        if (el?.errors?.length > 0) {
                            const errName = el?.path + 'ErrMsg'
                            const errMsg = el?.errors?.join(' ')
                            const tran = translateField(fields, el?.path)
                            if (tran) {
                                const tranErrMsg = await errMsg.replaceAll(
                                    el?.path,
                                    tran
                                )
                                errors[errName] = tranErrMsg
                            } else {
                                errors[errName] = el?.errors?.join(' ')
                            }
                        }
                    }
                })
            }
            //// Start Watson Assistant Journey

            //// End Watson Assistant Journey
            dispatch(
                updateFormValidity({
                    digital_name: digital_name,
                    errors: errors,
                })
            )
            return false
        }
    }

    //////// Functional Methods

    // Handle Form Submission
    const handleSubmit = async () => {
        try {
            console.info('Contact Information: Submitted')

            setLoading(true)

            // Extract Form Data From Redux
            const submit = await extractSubmissionForm(digital_name, formsData)

            // Validate Form Submission Data
            const valid = await validation(submit)

            // Check If Form Data Is Valid
            if (valid) {
                console.info('Contact Information: Passed Valid')

                dispatch(
                    updateFormSystem({
                        digital_name: digital_name,
                        system: { valid: true },
                    })
                )
                setLoading(false)
                setNotification({
                    type: 'inline',
                    visible: false,
                    kind: '',
                    title: '',
                    subtitle: '',
                })

                // Navigate To Next Form
                navigate(submit_route ? submit_route : '/')
            } else {
                console.info('Contact Information: Failed Valid')

                dispatch(
                    updateFormSystem({
                        digital_name: digital_name,
                        system: { valid: false },
                    })
                )
                setLoading(false)
                setNotification({
                    type: 'inline',
                    visible: true,
                    kind: 'error',
                    title: 'Form Errors:',
                    subtitle:
                        'There are errors on the form (such as missing required info). Please scroll up and update the fields with red messages.',
                })

                console.log(
                    'Contact Information Failed Valid: There are errors on the form (such as missing required info). Please scroll up and update the fields with red messages.'
                )
            }
        } catch (error) {
            console.warn('Handle Submit Error:', error)
            return null
        }
    }

    // Handle Keyboard Enter As Submit (Method)
    const handleKeyDown = async (event) => {
        try {
            if (event?.key === 'Enter') {
                await handleSubmit()
            }
        } catch (error) {
            console.warn('Handle Down Key Error:', error)
            return null
        }
    }

    //////// Memoization

    //////// Effects/Side Effects

    // Reload Watson Assistant Journey on Reload
    useEffect(() => {
        if (instance?.tours) {
            instance.tours.endTour()
            instance.tours.startTour(
                'Registered Nursing Endorsement Contact Info Tour'
            )
        }
        return () => {}
    }, [instance])

    //////////////// Render & Return
    if (loading) {
        return (
            <>
                <Form
                    tabIndex={0}
                    onKeyDown={handleKeyDown}
                    onSubmit={handleSubmit}
                >
                    <LoadingComponent />
                </Form>
            </>
        )
    } else {
        return (
            <>
                <Form
                    tabIndex={0}
                    onKeyDown={handleKeyDown}
                    onSubmit={handleSubmit}
                >
                    <AdditionalContactInformationForm instance={instance} />

                    <AddContactFormComponent />

                    <NotificationComponent notification={notification} />

                    <div className="button-group">
                        <div className="button-group-item">
                            <ButtonComponent
                                click={handleSubmit}
                                title="Save Progress"
                            />
                        </div>
                    </div>
                </Form>
            </>
        )
    }
}

export default ContactInformationForm
